import { switchMap, concatMap, catchError, ignoreElements } from 'rxjs/operators'
import { defer, of, NEVER, Observable } from 'rxjs'
import { Epic, ofType } from 'redux-observable'
import { Capacitor } from '@capacitor/core'
import { setIsLogged } from '../../actions/auth/SetIsLogged'
import SessionStorage from '../../../storage/session-storage/SessionStorage'
import AuthStorage from '../../../storage/auth-storage/AuthStorage'
import { AuthRepository } from '../../../../repositories/auth/AuthRepository'
import { AuthApiDataHandler } from '../../../../data-handlers/auth/AuthApiDataHandler'
import { RequestLogin, requestLoginFailure, requestLoginSuccess } from '../../actions/auth/RequestLogin'
import AuthActionTypes from '../../actions/auth/AuthActionTypes'
import { Logout } from '../../actions/auth/Logout'
import { setCrashlyticsUserId } from '../../../crashlytics/crashlyticsService'
import { promoteStagedBundle } from '../../../../ota'
import { performLogoutCleanup } from '../../../auth/performLogoutCleanup'

// Constructed lazily, not at module scope: this file is reached from the `api`
// singleton's own import of `store` (api/index.ts -> store -> epics -> here), so an
// eager `new AuthApiDataHandler()` here reads the `api` binding while the module
// graph is still resolving that cycle. Webpack's module-concatenation ordering
// happened to place the AuthApiDataHandler class declaration itself after this line
// once performLogoutCleanup.ts's import was added, crashing every cold start with
// "Cannot access 'AuthApiDataHandler' before initialization" — reproduced on-device.
// Deferring construction until an epic actually runs sidesteps the cycle entirely.
let authRepository: AuthRepository | undefined

function getAuthRepository(): AuthRepository {
  if (!authRepository) {
    authRepository = new AuthRepository(new AuthApiDataHandler())
  }
  return authRepository
}

/**
 * Request api for login
 */
 export const requestLoginEpic = (
  action$: Observable<RequestLogin>
) =>
  action$.pipe(
    ofType(AuthActionTypes.REQUEST_LOGIN),
    switchMap((action) =>
      defer(
        () => getAuthRepository().requestAuth(action.payload.email, action.payload.password)
      ).pipe(
        switchMap((response) => defer(async () => {
          getAuthRepository().setAuth(response.token)
          await AuthStorage.set('basic.auth', {
            user: action.payload.email,
            token: response.token,
          })
          // Ties every subsequent crash report to the seller that hit it.
          void setCrashlyticsUserId(action.payload.email)
          return requestLoginSuccess(response.token)
        })),
        catchError((error) => of(
          requestLoginFailure(error)
        ))
      )
    )
  )

/**
 * Set token on app init
 */
export const setAuthOnInitEpic: Epic = (
  action$
) =>
  action$.pipe(
    ofType('APP_INIT'),
    // AuthStorage.get is async (the native branch is a Keystore round-trip), unlike
    // the plain sessionStorage read this replaced — the read itself has to live
    // inside this switchMap/defer, not a map(), or `data` below would be a pending
    // Promise (always truthy) instead of the awaited value, and every cold start
    // would wrongly restore a session that was never there.
    switchMap(() => defer(async () => {
      // Must match getBackend()'s exact condition in AuthStorage.ts — wipe the
      // plaintext copy only when the read below is actually going to the secure
      // backend instead. On an old APK that received this JS via OTA (native
      // platform true, plugin not yet registered), AuthStorage.get() falls back to
      // this SAME SessionStorage store; wiping it unconditionally here would delete
      // the token an instant before reading it back, forcing a re-login on every
      // cold start until that device gets the new APK.
      if (Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('SecureStorage')) {
        // Best-effort wipe of any plaintext copy left on disk from before this
        // migration. Never blocks or fails the restore below — a wipe failure just
        // means the stale plaintext lingers one more launch, not a broken login.
        try {
          SessionStorage.remove('basic.auth')
        } catch {
          // best-effort
        }
      }
      return AuthStorage.get<IAuthStored>('basic.auth')
    })),
    switchMap(
      (data?: IAuthStored) => {
        if (!data) {
          return NEVER
        }

        const {
          user,
          token,
        } = data

        getAuthRepository().setAuth(token)
        // A restored session skips the login epic, so re-attach the identity here
        // or every crash after a cold start would be anonymous.
        if (user) {
          void setCrashlyticsUserId(user)
        }
        return of(setIsLogged(true))
      }
    )
  )

/**
 * Logout side effects
 */
 export const logoutEpic = (
  action$: Observable<Logout>
) =>
  action$.pipe(
    ofType(AuthActionTypes.LOGOUT),
    // concatMap + defer(async) + ignoreElements, NOT tap, NOT switchMap. Whatever an
    // epic emits is dispatched as an action, so the historical `map(() => EMPTY)`
    // dispatched an Observable — Redux saw an object with no `type` and threw
    // "Minified Redux error #7" on every logout, leaving the store half-initialised
    // and the next screen partly rendered. This epic is pure side effects and must
    // emit nothing. tap() alone won't do here either: it doesn't await a returned
    // promise, and the AuthStorage removal below MUST complete before
    // promoteStagedBundle's reload — see the comment at that call. switchMap would
    // unsubscribe an in-flight logout if LOGOUT dispatched twice in quick
    // succession, but the underlying async function isn't actually cancellable —
    // the first run keeps executing, so two promoteStagedBundle calls could
    // overlap. concatMap serializes instead, running one logout fully before the
    // next starts.
    concatMap(() => defer(async () => {
      // Awaited, not fire-and-forget: promoteStagedBundle() below can reload the
      // WebView. Letting that race ahead of the storage removal inside this cleanup
      // could leave the reload reading a still-present token — see the comment in
      // performLogoutCleanup.ts.
      await performLogoutCleanup()

      // Logout is the earliest moment a staged update becomes safe to apply: the
      // session is gone, so there is no order, form, modal or print left to lose.
      // Without this the swap waits for capgo's background installNext or the next
      // cold start, and a tablet that is never closed can sit on stale code for days.
      // requireSafe because a seller can start logging back in immediately; the
      // predicate then reports unsafe and the bundle simply stays staged for the next
      // opportunity rather than reloading mid-login.
      // Awaited and deliberately last — this reloads the webview, so nothing after
      // it runs, and it must not start before the AuthStorage removal above lands.
      await promoteStagedBundle({ requireSafe: true })
    })),
    ignoreElements()
  )


export const authEpics = [
  setAuthOnInitEpic,
  requestLoginEpic,
  logoutEpic,
]

export interface IAuthStored {
  user: string,
  token: string,
}
